import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Logo, Stack, Text, SegmentedSelection, StyledContainer,
} from '@piwikpro/ui-components';
import { useNavigate } from '../../hooks/useNavigate';
import { App, AppType } from '../../model';
import FailedContent from './FailedContent/FailedContent';
import LoadingContent from './LoadingContent/LoadingContent';
import MobileContent from './MobileContent/MobileContent';
import WebsiteContent from './WebsiteContent/WebsiteContent';
import { FullscreenOverlay } from '../FullscreenOverlay/FullscreenOverlay';

interface Props {
  isLoading: boolean
  app: App
  apps: App[]
  failed: boolean
}

const FirstAppTrial = ({
  isLoading,
  app,
  apps,
  failed,
}: Props) => {
  const { t } = useTranslation(['administration']);
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState<AppType>('website');

  const handleSegmentedSelectionChange = (value: string) => {
    setSelectedOption(value as AppType);
  };

  useEffect(() => {
    if (apps.length === 0) { return; }
    const firstNotDemoApp = apps.find(singleApp => !singleApp.attributes.name.includes('Demo'));
    navigate({
      to: '/administration/apps/$appId/data-collection',
      params: { appId: firstNotDemoApp!.id },
      search: { __adminOverlay: 'o/first-app' },
    });
  }, [apps]);

  return (
    <FullscreenOverlay>
      <StyledContainer backgroundImage="square-dot-gradient">
        <div style={{
          width: '660px', margin: '0 auto', padding: '32px 0', height: 'calc(100vh - 64px)',
        }}
        >
          <div data-ppuc-id="ProductBar.Logo" style={{ marginBottom: 93, textAlign: 'center' }}>
            <Logo height={20} />
          </div>
          {isLoading && <LoadingContent />}
          {failed && <FailedContent />}
          {!isLoading && !failed && (
            <>
              <Stack vertical spacing="wide" horizontalAlignment="middle">
                <Stack.Item>
                  <Text as="h1" size={24} weight={700} wrapType="noWrapAndCutText" dataId="Header.large">
                    {t('apps.appCreator.firstAppTrial.welcomeTitle')}
                  </Text>
                </Stack.Item>
                <Stack.Item previousSpacing="default">
                  <Text as="p" color="InkLighter" align="center">
                    {t('apps.appCreator.firstAppTrial.welcomeSubtitle1')}
                  </Text>
                  <Text as="p" color="InkLighter" align="center">
                    {t('apps.appCreator.firstAppTrial.welcomeSubtitle2')}
                  </Text>
                </Stack.Item>
                <Stack.Item previousSpacing="extraWide" fill>
                  <SegmentedSelection
                    fullWidth
                    items={[
                      { value: 'website', name: t('apps.appCreator.firstAppTrial.websiteOption') },
                      { value: 'mobile-app', name: t('apps.appCreator.firstAppTrial.mobileAppOption') },
                    ]}
                    value={selectedOption}
                    onChange={handleSegmentedSelectionChange}
                  />
                </Stack.Item>
                <Stack.Item />
              </Stack>
              {selectedOption === 'website'
                ? <WebsiteContent appFirstUrl={app.attributes.urls[0]} appId={app.id} />
                : <MobileContent appFirstUrl={app.attributes.urls[0]} appId={app.id} />}
            </>
          )}
        </div>
      </StyledContainer>
    </FullscreenOverlay>
  );
};

export default FirstAppTrial;
